import React from 'react';
import {hydrate, render} from 'react-dom';
import {Provider} from 'react-redux';
import {loadableReady} from '@loadable/component';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {configureStore} from '../../shared/startup/configureStore';
import {withErrorBoundary} from '../../shared/components/common/error-boundary/ErrorBoundary';
import LazyLoad from '../../shared/components/common/lazy-load/LazyLoad';
import {LAZY_LOAD_OFFSET} from '../../shared/utils/constants';
import {SearchBoxLite} from '../../shared/components/common/searchboxlite/SearchBoxLite';
import {SearchFormContainer} from '../../shared/components/common/search-form/SearchForm';
import {TESTS, getValue} from '../../shared/utils/abacus';
import {getActionLocation} from '../../shared/utils/edap';
import client from './client';
import {ApolloProvider} from 'react-apollo';
import {LocalizationContextProvider} from '@homeaway/react-localization-components';
import {register} from '../../shared/utils/mab-client';

const {DEFAULT: LAZY_LOAD_OFFSET_DEFAULT} = LAZY_LOAD_OFFSET;

if (process.env.NODE_ENV === 'development') {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React, {
        trackAllPureComponents: true,
        logOwnerReasons: true,
        collapseGroups: true,
    });
}

const getTemplateComponent = (template) =>
    process.env.NODE_ENV === 'development'
        ? import(
              /* webpackChunkName: "content-[request]" */
              /* webpackPreload: true */
              /* webpackInclude: /\.js$/ */
              /* webpackExclude: /\.test\.js$/ */
              `../../shared/templates/${template}`
          ).then(({Template}) => Template)
        : import(
              /* webpackChunkName: "content" */
              /* webpackPreload: true */
              '../../shared/App'
          ).then(({App}) => App);

const hydrateContent = (store) => {
    const {
        config: {
            template,
            url: {prefix},
        },
        siteContext,
    } = store.getState();

    const whenTemplate = getTemplateComponent(template);

    const whenLoadableReady = loadableReady();

    Promise.all([whenTemplate, whenLoadableReady]).then(([Template]) => {
        hydrate(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        <Template />
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.content-root')
        );
    });
};

/* eslint consistent-return:0 */
export default function hydrateApp() {
    // Grab the state from a global injected into server-generated HTML
    const preloadedState = window.__PRELOADED_STATE__;

    // Create Redux store with initial state
    const store = configureStore({
        initialState: preloadedState,
    });

    const {
        abacus,
        config: {
            template,
            url: {prefix},
        },
        options,
        siteContext,
        mab,
    } = store.getState();

    window.__SITE_CONTEXT__ = siteContext;

    register(window.edap, mab);

    const searchFormTest =
        TESTS[`SEARCH_FORM_${getActionLocation(template).toUpperCase()}`];
    const showSearchForm = getValue(abacus, searchFormTest) === 1;

    AbacusClientUtils.initialize(abacus);

    if (abacus[TESTS.POLYFILL_SERVICE]) {
        AbacusClientUtils.logExperiment(abacus[TESTS.POLYFILL_SERVICE]);
    }

    const SearchComponent = showSearchForm
        ? SearchFormContainer
        : SearchBoxLite;

    hydrate(
        <Provider store={store}>
            {withErrorBoundary(<SearchComponent />)}
        </Provider>,
        document.querySelector('.searchbox-root')
    );

    if (options.breadcrumbLocation === 'header') {
        import(
            /* webpackChunkName: "breadcrumbs" */
            '../../shared/modules/BreadCrumbs'
        ).then((module) => {
            const Breadcrumbs = module.default;
            hydrate(
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<Breadcrumbs isInHeader />)}
                    </LocalizationContextProvider>
                </Provider>,
                document.querySelector('.breadcrumbs-root')
            );
        });
    }

    import(
        /* webpackChunkName: "site-header" */
        /* webpackPreload: true */
        '../../shared/components/common/site-header/SiteHeader'
    ).then((module) => {
        const SiteHeader = module.default;
        render(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<SiteHeader />)}
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.site-header-root')
        );
    });

    hydrateContent(store);

    import(
        /* webpackChunkName: "site-footer" */
        '../../shared/components/common/site-footer/AsyncSiteFooter'
    ).then(({AsyncSiteFooter}) => {
        render(
            <Provider store={store}>
                <LazyLoad offset={LAZY_LOAD_OFFSET_DEFAULT}>
                    {withErrorBoundary(<AsyncSiteFooter />)}
                </LazyLoad>
            </Provider>,
            document.querySelector('.site-footer-root')
        );
    });
}
